/**
 * GET /api/v2/sites/{siteId}/vlan-path?vlan=N | ?ssid=<name> -- the end-to-end VLAN path check
 * (OP#1818). READ-ONLY on the server (stored reads only, no device session), so it is safe to
 * fetch on demand from any role that can see the site. Types and pure logic: lib/vlanPath.ts.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "./client";
import { qk } from "./queryKeys";
import { useSession } from "@/stores/session";
import { pathQuery, type VlanPathResult } from "@/lib/vlanPath";

/** The check for one VLAN or one SSID. Only fetched while `enabled` (the dialog is open). */
export function useVlanPath(
  siteId: string | null,
  q: { vlan?: number | null; ssid?: string | null },
  enabled: boolean,
) {
  const orgId = useSession((s) => s.currentOrgId);
  const query = pathQuery(q);
  return useQuery({
    // Nested under the site's VLAN keys so a site VLAN change invalidates it too.
    queryKey: [...qk.sites.siteVlans(orgId, siteId), "path", query] as const,
    queryFn: ({ signal }) =>
      api.get<VlanPathResult>(`/sites/${siteId}/vlan-path?${query}`, undefined, signal),
    enabled: enabled && !!orgId && !!siteId,
    // A path describes live devices: never serve an old answer as current.
    staleTime: 0,
    retry: false,
  });
}
